/**
 * Forecast Types
 *
 * Forecast categories on deals, AI-derived win-probability predictions, and
 * manual rep forecast submissions. DealForecastFeatures is the internal signal
 * bundle assembled per deal and fed to the prediction model - shared so the
 * service and its tests agree on shape.
 */
import { z } from 'zod';
// =============================================================================
// Primitives
// =============================================================================
/** Manual forecast bucket a deal is placed in. Derived from FORECAST_CATEGORIES. */
export const FORECAST_CATEGORIES = ['commit', 'best_case', 'pipeline', 'omitted'];
// Display metadata for UI. Record<ForecastCategory, ...> so a new bucket is a
// compile error until it gets a label here.
export const ForecastCategoryMetadata = {
    commit: { label: 'Commit' },
    best_case: { label: 'Best Case' },
    pipeline: { label: 'Pipeline' },
    omitted: { label: 'Omitted' },
};
/** Confidence band on an AI prediction. */
export const FORECAST_CONFIDENCE = ['low', 'medium', 'high'];
// =============================================================================
// Zod schemas
// =============================================================================
export const ForecastFactorSchema = z.object({
    label: z.string(),
    impact: z.enum(['positive', 'negative', 'neutral']),
    weight: z.number(),
});
export const DealForecastPredictionSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    dealId: z.string().uuid(),
    predictedProbability: z.number().min(0).max(100),
    confidence: z.enum(FORECAST_CONFIDENCE),
    topFactors: z.array(ForecastFactorSchema),
    rationale: z.string(),
    modelId: z.string(),
    signalCount: z.number(),
    createdAt: z.string().datetime(),
});
export const ForecastSubmissionSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    userId: z.string(),
    period: z.string(),
    category: z.enum(FORECAST_CATEGORIES),
    amount: z.number(),
    dealIds: z.array(z.string()),
    submittedAt: z.string().datetime(),
});
